(function () {
  var TOKEN_KEY = "flowPlusPortalToken";
  var EMAIL_KEY = "flowPlusPortalEmail";
  var API_PATH = "/v1/public/flow-plus-portal";
  var RESEND_CODE_COOLDOWN_SECONDS = 60;

  function FlowPlusPortal(root) {
    this.root = root;
    this.apiBaseUrl = normalizeApiBaseUrl(root.dataset.apiBaseUrl || "");
    this.supportUrl = root.dataset.supportUrl || "/pages/contact";
    this.token = readStorage(TOKEN_KEY);
    this.email = readStorage(EMAIL_KEY) || "";
    this.customer = null;
    this.subscription = null;
    this.upcomingCharge = null;
    this.history = [];
    this.appActivation = null;
    this.cancellationSurvey = null;
    this.selectedCancellationOptionId = "";
    this.cancellationTriggerButton = null;
    this.resendCooldownTimer = null;
    this.resendCooldownExpiresAt = 0;
    this.resendButtonDefaultText = "";
    this.bind();
    this.init();
  }

  FlowPlusPortal.prototype.bind = function () {
    var emailForm = this.root.querySelector("[data-email-form]");
    var codeForm = this.root.querySelector("[data-code-form]");
    var resendButton = this.root.querySelector("[data-resend-code]");
    var retryButton = this.root.querySelector("[data-retry]");
    var cancellationModal = this.root.querySelector("[data-cancellation-modal]");
    var cancellationCloseButtons = this.root.querySelectorAll("[data-cancellation-close], [data-cancellation-keep]");
    var cancellationConfirmButton = this.root.querySelector("[data-cancellation-confirm]");
    var cancellationOptions = this.root.querySelector("[data-cancellation-options]");

    if (emailForm) {
      emailForm.addEventListener("submit", this.handleEmailSubmit.bind(this));
    }

    if (codeForm) {
      codeForm.addEventListener("submit", this.handleCodeSubmit.bind(this));
    }

    if (resendButton) {
      resendButton.addEventListener("click", this.handleResendCode.bind(this));
    }

    if (retryButton) {
      retryButton.addEventListener("click", this.init.bind(this));
    }

    if (cancellationModal) {
      cancellationModal.addEventListener("click", function (event) {
        if (event.target === cancellationModal) {
          this.closeCancellationDialog();
        }
      }.bind(this));
    }

    cancellationCloseButtons.forEach(function (button) {
      button.addEventListener("click", this.closeCancellationDialog.bind(this));
    }, this);

    if (cancellationConfirmButton) {
      cancellationConfirmButton.addEventListener("click", this.confirmCancellation.bind(this));
    }

    if (cancellationOptions) {
      cancellationOptions.addEventListener("change", function (event) {
        if (event.target && event.target.name === "flow-plus-cancellation-reason") {
          this.selectedCancellationOptionId = event.target.value;
          this.setCancellationMessage("", null);
          this.updateCancellationConfirmState();
        }
      }.bind(this));
    }

    document.addEventListener("keydown", function (event) {
      if (event.key === "Escape" && cancellationModal && !cancellationModal.hidden) {
        this.closeCancellationDialog();
      }
    }.bind(this));

    this.root.querySelectorAll("[data-action]").forEach(function (button) {
      button.addEventListener("click", this.handleAction.bind(this));
    }, this);
  };

  FlowPlusPortal.prototype.init = function () {
    this.showView("loading");
    this.setSavedEmail();

    if (!this.apiBaseUrl) {
      this.showView("setup");
      return;
    }

    if (!this.token) {
      this.showLogin();
      return;
    }

    this.loadPortal();
  };

  FlowPlusPortal.prototype.showLogin = function () {
    this.showView("login");
  };

  FlowPlusPortal.prototype.handleEmailSubmit = function (event) {
    event.preventDefault();
    var form = event.currentTarget;
    var email = String(new FormData(form).get("email") || "").trim();

    if (!email) return;

    this.setAuthMessage("Sending verification code...", null);
    this.setFormDisabled(form, true);
    this.setResendBusy(true);

    this.request("/auth/start", {
      method: "POST",
      body: { email: email },
      auth: false,
    })
      .then(function () {
        this.email = email;
        writeStorage(EMAIL_KEY, email);
        this.showCodeForm();
        this.startResendCooldown();
        this.setAuthMessage("Check your email for the verification code.", "success");
      }.bind(this))
      .catch(function (error) {
        this.setAuthMessage(normalizeError(error, "Unable to send a code."), "error");
      }.bind(this))
      .finally(function () {
        this.setFormDisabled(form, false);
        if (!this.isResendCooldownActive()) {
          this.setResendBusy(false);
        }
      }.bind(this));
  };

  FlowPlusPortal.prototype.handleCodeSubmit = function (event) {
    event.preventDefault();
    var form = event.currentTarget;
    var code = String(new FormData(form).get("code") || "").trim();
    var email = this.email || readStorage(EMAIL_KEY) || "";

    if (!code || !email) return;

    this.setAuthMessage("Verifying code...", null);
    this.setFormDisabled(form, true);

    this.request("/auth/verify", {
      method: "POST",
      body: {
        email: email,
        code: code,
      },
      auth: false,
    })
      .then(function (result) {
        if (!result || !result.token) {
          throw new Error("Verification did not return a portal session");
        }

        this.setToken(result.token);
        return this.loadPortal();
      }.bind(this))
      .catch(function (error) {
        this.setAuthMessage(normalizeError(error, "That code did not work."), "error");
      }.bind(this))
      .finally(function () {
        this.setFormDisabled(form, false);
        if (this.isResendCooldownActive()) {
          this.updateResendCooldown();
        }
      }.bind(this));
  };

  FlowPlusPortal.prototype.handleResendCode = function () {
    if (this.isResendCooldownActive()) return;

    var emailInput = this.root.querySelector("#FlowPlusPortalEmail");
    var emailForm = this.root.querySelector("[data-email-form]");
    if (!emailInput || !emailInput.value || !emailForm) return;

    emailForm.dispatchEvent(
      new Event("submit", { bubbles: true, cancelable: true }),
    );
  };

  FlowPlusPortal.prototype.setToken = function (token) {
    this.token = token;
    writeStorage(TOKEN_KEY, token);
    this.clearResendCooldown();
  };

  FlowPlusPortal.prototype.clearToken = function () {
    this.token = "";
    removeStorage(TOKEN_KEY);
  };

  FlowPlusPortal.prototype.loadPortal = function () {
    this.showView("loading");

    return this.request("/overview", {
      method: "GET",
      auth: true,
    })
      .then(function (overview) {
        this.customer = overview.customer || null;
        this.subscription = overview.subscription || null;
        this.upcomingCharge = overview.upcoming_charge || null;
        this.history = Array.isArray(overview.history) ? overview.history : [];
        this.appActivation = overview.app_activation || null;
        this.cancellationSurvey = overview.cancellation_survey || null;

        if (!this.subscription) {
          this.showView("empty");
          return;
        }

        this.renderPortal();
        this.showView("portal");
      }.bind(this))
      .catch(function (error) {
        if (error && error.status === 401) {
          this.clearToken();
          this.showLogin();
          return;
        }

        this.showError(normalizeError(error, "Unable to load your Flow+ membership."));
      }.bind(this));
  };

  FlowPlusPortal.prototype.renderPortal = function () {
    var subscription = this.subscription;
    var status = normalizeStatus(subscription.status);
    var isCancelled = status === "cancelled" || status === "expired";

    setText(this.root, "[data-plan-name]", subscription.product_title || "Flow+");
    setText(this.root, "[data-customer-email]", this.customer && this.customer.email ? this.customer.email : "--");
    setText(this.root, "[data-renewal-date]", formatDate(subscription.next_charge_scheduled_at, isCancelled ? "No active renewal" : "--"));
    setText(this.root, "[data-plan-price]", formatMoney(subscription.price, subscription.currency));
    setText(this.root, "[data-billing-status]", sentenceCase(subscription.billing_status || status || "active"));

    var pill = this.root.querySelector("[data-status-pill]");
    if (pill) {
      pill.textContent = isCancelled ? "Renewal cancelled" : sentenceCase(status || "Active");
      pill.dataset.tone = isCancelled ? "danger" : status === "active" ? "success" : "warning";
    }

    var cancelButton = this.root.querySelector('[data-action="cancel"]');
    var reactivateButton = this.root.querySelector('[data-action="reactivate"]');
    if (cancelButton) cancelButton.hidden = isCancelled;
    if (reactivateButton) reactivateButton.hidden = !isCancelled;

    this.renderAppActivation();
    this.renderCancellationDialog();
    this.renderUpcomingCharge();
    this.renderHistory();
  };

  FlowPlusPortal.prototype.getActiveCancellationSurvey = function () {
    var survey = this.cancellationSurvey;
    if (!survey || !survey.enabled || !Array.isArray(survey.options) || !survey.options.length) {
      return null;
    }

    return survey;
  };

  FlowPlusPortal.prototype.renderCancellationDialog = function () {
    var survey = this.getActiveCancellationSurvey();
    var title = this.root.querySelector("[data-cancellation-title]");
    var description = this.root.querySelector("[data-cancellation-description]");
    var question = this.root.querySelector("[data-cancellation-question]");
    var fieldset = this.root.querySelector("[data-cancellation-fieldset]");
    var options = this.root.querySelector("[data-cancellation-options]");
    var confirmButton = this.root.querySelector("[data-cancellation-confirm]");
    var keepButton = this.root.querySelector("[data-cancellation-keep]");

    if (title) {
      title.textContent = survey && survey.confirm_title
        ? survey.confirm_title
        : "Cancel Flow+ renewal?";
    }

    if (description) {
      description.textContent = survey && survey.confirm_description
        ? survey.confirm_description
        : "You will keep access through your current paid period.";
    }

    if (question) {
      question.textContent = survey && survey.question
        ? survey.question
        : "Why are you cancelling?";
    }

    if (confirmButton) {
      confirmButton.textContent = survey && survey.confirm_button_label
        ? survey.confirm_button_label
        : "Cancel renewal";
    }

    if (keepButton) {
      keepButton.textContent = survey && survey.keep_button_label
        ? survey.keep_button_label
        : "Keep Flow+";
    }

    if (!fieldset || !options) {
      return;
    }

    fieldset.hidden = !survey;
    if (!survey) {
      options.innerHTML = "";
      this.selectedCancellationOptionId = "";
      this.updateCancellationConfirmState();
      return;
    }

    options.innerHTML = survey.options.map(function (option) {
      var optionId = "FlowPlusCancellationOption-" + String(option.id || "").replace(/[^a-zA-Z0-9_-]/g, "");
      return [
        '<label class="flow-plus-portal__survey-option" for="' + escapeHtml(optionId) + '">',
        '<input id="' + escapeHtml(optionId) + '" type="radio" name="flow-plus-cancellation-reason" value="' + escapeHtml(option.id) + '">',
        '<span>' + escapeHtml(option.label) + "</span>",
        "</label>",
      ].join("");
    }).join("");

    this.selectedCancellationOptionId = "";
    this.updateCancellationConfirmState();
  };

  FlowPlusPortal.prototype.openCancellationDialog = function (button) {
    if (!this.subscription) return;

    this.cancellationTriggerButton = button || null;
    this.renderCancellationDialog();
    this.setCancellationMessage("", null);

    var modal = this.root.querySelector("[data-cancellation-modal]");
    if (!modal) return;

    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");

    var firstRadio = modal.querySelector('input[name="flow-plus-cancellation-reason"]');
    var keepButton = modal.querySelector("[data-cancellation-keep]");
    window.setTimeout(function () {
      (firstRadio || keepButton || modal).focus();
    }, 0);
  };

  FlowPlusPortal.prototype.closeCancellationDialog = function () {
    var modal = this.root.querySelector("[data-cancellation-modal]");
    if (modal) {
      modal.hidden = true;
      modal.setAttribute("aria-hidden", "true");
    }

    this.selectedCancellationOptionId = "";
    this.setCancellationMessage("", null);

    if (this.cancellationTriggerButton) {
      this.cancellationTriggerButton.focus();
    }
  };

  FlowPlusPortal.prototype.updateCancellationConfirmState = function () {
    var confirmButton = this.root.querySelector("[data-cancellation-confirm]");
    if (!confirmButton) return;

    confirmButton.disabled = !!this.getActiveCancellationSurvey() && !this.selectedCancellationOptionId;
  };

  FlowPlusPortal.prototype.renderUpcomingCharge = function () {
    var row = this.root.querySelector("[data-upcoming-payment-row]");
    var value = this.root.querySelector("[data-upcoming-payment]");
    if (!row || !value) return;

    if (!this.upcomingCharge || !this.upcomingCharge.scheduled_at) {
      row.hidden = true;
      value.textContent = "--";
      return;
    }

    var amount = formatMoney(this.upcomingCharge.amount, this.upcomingCharge.currency);
    var date = formatDate(this.upcomingCharge.scheduled_at, "--");
    value.textContent = amount + " on " + date;
    row.hidden = false;
  };

  FlowPlusPortal.prototype.renderHistory = function () {
    var list = this.root.querySelector("[data-history-list]");
    if (!list) return;

    if (!this.history.length) {
      list.innerHTML = '<p class="flow-plus-portal__muted">No completed Flow+ charges yet.</p>';
      return;
    }

    list.innerHTML = this.history.map(function (item) {
      var status = sentenceCase(item.status || "charge");
      var date = formatDate(item.processed_at, "--");
      var amount = formatMoney(item.amount, item.currency);

      return [
        '<div class="flow-plus-portal__history-row">',
        '<div class="flow-plus-portal__history-main">',
        '<span class="flow-plus-portal__history-title">Flow+ billing</span>',
        '<span class="flow-plus-portal__history-meta">' + escapeHtml(status + " · " + date) + "</span>",
        "</div>",
        '<span class="flow-plus-portal__history-amount">' + escapeHtml(amount) + "</span>",
        "</div>",
      ].join("");
    }).join("");
  };

  FlowPlusPortal.prototype.handleAction = function (event) {
    var action = event.currentTarget.dataset.action;

    if (action === "billing") {
      this.sendBillingUpdateEmail(event.currentTarget);
      return;
    }

    if (action === "cancel") {
      this.openCancellationDialog(event.currentTarget);
      return;
    }

    if (action === "reactivate") {
      this.reactivate(event.currentTarget);
      return;
    }

    if (action === "activation") {
      this.revealActivationCode(event.currentTarget);
    }
  };

  FlowPlusPortal.prototype.sendBillingUpdateEmail = function (button) {
    this.setActionMessage("Sending secure billing update email...", null);
    this.setButtonBusy(button, true);

    this.request("/billing/update-email", {
      method: "POST",
      body: {},
      auth: true,
    })
      .then(function () {
        this.setActionMessage("Check your email for the secure billing update link.", "success");
      }.bind(this))
      .catch(function (error) {
        this.handleActionError(error, "Unable to send billing update email.");
      }.bind(this))
      .finally(function () {
        this.setButtonBusy(button, false);
      }.bind(this));
  };

  FlowPlusPortal.prototype.confirmCancellation = function (event) {
    var button = event.currentTarget;
    var activeSurvey = this.getActiveCancellationSurvey();

    if (!this.subscription) return;

    if (activeSurvey && !this.selectedCancellationOptionId) {
      this.setCancellationMessage("Choose a reason before cancelling Flow+.", "error");
      return;
    }

    this.setCancellationMessage("Cancelling renewal...", null);
    this.setButtonBusy(button, true);
    if (this.cancellationTriggerButton) {
      this.setButtonBusy(this.cancellationTriggerButton, true);
    }

    this.request("/subscriptions/" + encodeURIComponent(this.subscription.id) + "/cancel", {
      method: "POST",
      body: activeSurvey ? { survey_option_id: this.selectedCancellationOptionId } : {},
      auth: true,
    })
      .then(function (overview) {
        this.closeCancellationDialog();
        this.applyOverview(overview);
        this.setActionMessage("Flow+ renewal has been cancelled.", "success");
      }.bind(this))
      .catch(function (error) {
        if (error && error.status === 401) {
          this.clearToken();
          this.showLogin();
          return;
        }

        this.setCancellationMessage(normalizeError(error, "Unable to cancel renewal."), "error");
      }.bind(this))
      .finally(function () {
        this.setButtonBusy(button, false);
        if (this.cancellationTriggerButton) {
          this.setButtonBusy(this.cancellationTriggerButton, false);
        }
      }.bind(this));
  };

  FlowPlusPortal.prototype.reactivate = function (button) {
    if (!this.subscription) return;

    this.setActionMessage("Reactivating Flow+...", null);
    this.setButtonBusy(button, true);

    this.request("/subscriptions/" + encodeURIComponent(this.subscription.id) + "/reactivate", {
      method: "POST",
      body: {},
      auth: true,
    })
      .then(function (overview) {
        this.applyOverview(overview);
        this.setActionMessage("Flow+ has been reactivated.", "success");
      }.bind(this))
      .catch(function (error) {
        this.handleActionError(error, "Unable to reactivate Flow+.");
      }.bind(this))
      .finally(function () {
        this.setButtonBusy(button, false);
      }.bind(this));
  };

  FlowPlusPortal.prototype.revealActivationCode = function (button) {
    if (!this.subscription || !this.appActivation || this.appActivation.status !== "available") {
      this.setActivationMessage("This subscription needs support before an app activation code can be shown.", "error");
      return;
    }

    this.setActivationMessage("Preparing activation code...", null);
    this.setButtonBusy(button, true);

    this.request("/subscriptions/" + encodeURIComponent(this.subscription.id) + "/claim-code", {
      method: "POST",
      body: {},
      auth: true,
    })
      .then(function (result) {
        this.renderActivationCode(result);
        this.setActivationMessage("Use this code in the Flow Alarm app.", "success");
      }.bind(this))
      .catch(function (error) {
        this.handleActivationError(error, "Unable to show activation code.");
      }.bind(this))
      .finally(function () {
        this.setButtonBusy(button, false);
      }.bind(this));
  };

  FlowPlusPortal.prototype.applyOverview = function (overview) {
    this.customer = overview.customer || null;
    this.subscription = overview.subscription || null;
    this.upcomingCharge = overview.upcoming_charge || null;
    this.history = Array.isArray(overview.history) ? overview.history : [];
    this.appActivation = overview.app_activation || null;
    this.cancellationSurvey = overview.cancellation_survey || null;

    if (!this.subscription) {
      this.showView("empty");
      return;
    }

    this.renderPortal();
    this.showView("portal");
  };

  FlowPlusPortal.prototype.renderAppActivation = function () {
    var section = this.root.querySelector("[data-app-activation]");
    if (!section) return;

    var button = this.root.querySelector("[data-activation-button]");
    var codeRow = this.root.querySelector("[data-activation-code-row]");
    var code = this.root.querySelector("[data-activation-code]");
    var deepLink = this.root.querySelector("[data-activation-deep-link]");
    var copy = this.root.querySelector("[data-activation-copy]");
    var activation = this.appActivation;
    var isAvailable = activation && activation.status === "available";
    var hasClaimCode = activation && activation.claim_code;

    if (copy) {
      copy.textContent = activation && activation.message
        ? activation.message
        : "We could not find an app activation code for this subscription. Contact support and include the checkout email so we can look it up.";
    }

    if (code) {
      code.textContent = "";
    }

    if (deepLink) {
      deepLink.href = "#";
      deepLink.hidden = true;
    }

    if (button) {
      button.hidden = !isAvailable || !!hasClaimCode;
      button.disabled = false;
      button.setAttribute("aria-busy", "false");
    }

    if (codeRow) {
      codeRow.hidden = true;
    }

    if (hasClaimCode) {
      this.renderActivationCode(activation);
    }

    this.setActivationMessage("", null);
  };

  FlowPlusPortal.prototype.renderActivationCode = function (result) {
    var codeRow = this.root.querySelector("[data-activation-code-row]");
    var code = this.root.querySelector("[data-activation-code]");
    var deepLink = this.root.querySelector("[data-activation-deep-link]");
    var claimCode = result && result.claim_code ? String(result.claim_code) : "";
    var appDeepLink = result && result.app_deep_link ? String(result.app_deep_link) : "";

    if (code) {
      code.textContent = claimCode;
    }

    if (deepLink) {
      deepLink.href = appDeepLink || "#";
      deepLink.hidden = !appDeepLink;
    }

    if (codeRow) {
      codeRow.hidden = !claimCode;
    }
  };

  FlowPlusPortal.prototype.handleActionError = function (error, fallback) {
    if (error && error.status === 401) {
      this.clearToken();
      this.showLogin();
      return;
    }

    this.setActionMessage(normalizeError(error, fallback), "error");
  };

  FlowPlusPortal.prototype.handleActivationError = function (error, fallback) {
    if (error && error.status === 401) {
      this.clearToken();
      this.showLogin();
      return;
    }

    this.setActivationMessage(normalizeError(error, fallback), "error");
  };

  FlowPlusPortal.prototype.request = function (path, options) {
    var requestOptions = options || {};
    var headers = {
      Accept: "application/json",
    };
    var fetchOptions = {
      method: requestOptions.method || "GET",
      headers: headers,
    };

    if (requestOptions.auth) {
      if (!this.token) {
        return Promise.reject(withStatus(new Error("Missing portal session"), 401));
      }
      headers.Authorization = "Bearer " + this.token;
    }

    if (requestOptions.body !== undefined) {
      headers["Content-Type"] = "application/json";
      fetchOptions.body = JSON.stringify(requestOptions.body);
    }

    return window.fetch(this.apiBaseUrl + API_PATH + path, fetchOptions)
      .then(function (response) {
        return response.text().then(function (rawBody) {
          var body = parseJson(rawBody);

          if (!response.ok) {
            throw withStatus(
              new Error(resolveApiErrorMessage(body, response.statusText)),
              response.status,
            );
          }

          return body || {};
        });
      });
  };

  FlowPlusPortal.prototype.showView = function (name) {
    this.root.querySelectorAll("[data-view]").forEach(function (view) {
      view.hidden = view.dataset.view !== name;
    });
  };

  FlowPlusPortal.prototype.showError = function (message) {
    setText(this.root, "[data-error-message]", message);
    this.showView("error");
  };

  FlowPlusPortal.prototype.showCodeForm = function () {
    var codeForm = this.root.querySelector("[data-code-form]");
    if (codeForm) codeForm.hidden = false;
  };

  FlowPlusPortal.prototype.startResendCooldown = function () {
    var button = this.root.querySelector("[data-resend-code]");
    if (!button) return;

    if (!this.resendButtonDefaultText) {
      this.resendButtonDefaultText = button.textContent;
    }

    if (this.resendCooldownTimer) {
      window.clearTimeout(this.resendCooldownTimer);
    }

    this.resendCooldownExpiresAt = Date.now() + (RESEND_CODE_COOLDOWN_SECONDS * 1000);
    this.updateResendCooldown();
  };

  FlowPlusPortal.prototype.clearResendCooldown = function () {
    if (this.resendCooldownTimer) {
      window.clearTimeout(this.resendCooldownTimer);
      this.resendCooldownTimer = null;
    }

    this.resendCooldownExpiresAt = 0;
    this.setResendBusy(false);
  };

  FlowPlusPortal.prototype.isResendCooldownActive = function () {
    return this.resendCooldownExpiresAt > Date.now();
  };

  FlowPlusPortal.prototype.updateResendCooldown = function () {
    var button = this.root.querySelector("[data-resend-code]");
    if (!button) return;

    var remainingSeconds = Math.ceil((this.resendCooldownExpiresAt - Date.now()) / 1000);
    if (remainingSeconds <= 0) {
      this.clearResendCooldown();
      return;
    }

    button.disabled = true;
    button.setAttribute("aria-busy", "false");
    button.textContent = "Send a new code (" + remainingSeconds + "s)";

    if (this.resendCooldownTimer) {
      window.clearTimeout(this.resendCooldownTimer);
    }

    this.resendCooldownTimer = window.setTimeout(
      this.updateResendCooldown.bind(this),
      1000,
    );
  };

  FlowPlusPortal.prototype.setResendBusy = function (busy) {
    var button = this.root.querySelector("[data-resend-code]");
    if (!button) return;

    button.disabled = !!busy;
    button.setAttribute("aria-busy", busy ? "true" : "false");
    if (!busy && this.resendButtonDefaultText) {
      button.textContent = this.resendButtonDefaultText;
    }
  };

  FlowPlusPortal.prototype.setSavedEmail = function () {
    var emailInput = this.root.querySelector("#FlowPlusPortalEmail");
    if (emailInput && this.email) {
      emailInput.value = this.email;
    }
  };

  FlowPlusPortal.prototype.setAuthMessage = function (message, tone) {
    this.setMessage("[data-auth-message]", message, tone);
  };

  FlowPlusPortal.prototype.setActionMessage = function (message, tone) {
    this.setMessage("[data-action-message]", message, tone);
  };

  FlowPlusPortal.prototype.setActivationMessage = function (message, tone) {
    this.setMessage("[data-activation-message]", message, tone);
  };

  FlowPlusPortal.prototype.setCancellationMessage = function (message, tone) {
    this.setMessage("[data-cancellation-message]", message, tone);
  };

  FlowPlusPortal.prototype.setMessage = function (selector, message, tone) {
    var element = this.root.querySelector(selector);
    if (!element) return;
    element.textContent = message || "";
    if (tone) {
      element.dataset.tone = tone;
    } else {
      delete element.dataset.tone;
    }
  };

  FlowPlusPortal.prototype.setFormDisabled = function (form, disabled) {
    form.querySelectorAll("button, input").forEach(function (element) {
      element.disabled = disabled;
    });
  };

  FlowPlusPortal.prototype.setButtonBusy = function (button, busy) {
    if (!button) return;
    button.disabled = busy;
    button.setAttribute("aria-busy", busy ? "true" : "false");
  };

  function normalizeApiBaseUrl(value) {
    return String(value || "").trim().replace(/\/+$/, "");
  }

  function normalizeStatus(status) {
    return String(status || "").trim().toLowerCase();
  }

  function formatMoney(value, currency) {
    var number = Number(value);
    if (!Number.isFinite(number)) return "--";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(number);
  }

  function formatDate(value, fallback) {
    if (!value) return fallback;
    var date = new Date(value);
    if (Number.isNaN(date.getTime())) return fallback;

    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date);
  }

  function sentenceCase(value) {
    var text = String(value || "").replace(/_/g, " ").trim();
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : "--";
  }

  function setText(root, selector, text) {
    var element = root.querySelector(selector);
    if (element) element.textContent = text;
  }

  function readStorage(key) {
    try {
      return window.sessionStorage.getItem(key) || "";
    } catch (error) {
      return "";
    }
  }

  function writeStorage(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      return;
    }
  }

  function removeStorage(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      return;
    }
  }

  function parseJson(rawBody) {
    if (!rawBody || !rawBody.trim()) return null;
    try {
      return JSON.parse(rawBody);
    } catch (error) {
      return null;
    }
  }

  function resolveApiErrorMessage(body, fallback) {
    if (body && body.error && body.error.message) {
      return body.error.message;
    }

    return fallback || "Request failed";
  }

  function normalizeError(error, fallback) {
    if (error && error.message) return error.message;
    return fallback;
  }

  function withStatus(error, status) {
    error.status = status;
    return error;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  document.addEventListener("DOMContentLoaded", function () {
    document.querySelectorAll("[data-flow-plus-portal]").forEach(function (root) {
      new FlowPlusPortal(root);
    });
  });
})();
